import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { ColorRing } from "react-loader-spinner";

import { Button, Link } from "shared/components";
import { getParam } from "shared/lib/next";
import { BFC, isAPIError } from "shared/types";

import { useAuthContext } from "~/features/auth";

import { useEmailConfirmation } from "../../hooks/useEmailConfirmation";

export const ConfirmationEmailPage: BFC = () => {
  const { user: { confirmedEmail }, isLoading } = useAuthContext();
  const { confirmEmail, sendConfirmationMail, isConfirmEmailLoading } = useEmailConfirmation();
  const [failed, setFailed] = useState<boolean | null>(null);
  const [sent, setSent] = useState<boolean>(false);
  const router = useRouter();
  const confirmationToken = getParam(router.query, "confirmation_token");
  const returnTo = getParam(router.query, "return_to") || "/";

  useEffect(() => {
    if (isLoading) return;

    if (confirmationToken) {
      confirmEmail({ confirmationToken }).then(() => {
        setFailed(false);
      }).catch((e) => {
        // conflictの場合は、すでに確認済み
        if (isAPIError(e) && e.response.status === 409) {
          setFailed(false);
        } else {
          setFailed(true);
        }
      });
    }
  }, [confirmationToken, isLoading]);

  useEffect(() => {
    if (confirmedEmail) {
      const timeoutId = setTimeout(() => {
        router.push(returnTo);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [confirmedEmail]);

  const handleResend = useCallback(() => {
    sendConfirmationMail({ returnTo }).then(() => {
      setSent(true);
    });
  }, [sendConfirmationMail, returnTo]);

  if (isConfirmEmailLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <ColorRing />
      </div>
    );
  }

  return (
    <div className="bg-white p-3">
      {failed === false && confirmedEmail && (
        <div className="flex flex-col gap-4">
          <h1 className="font-bold text-center">メールアドレス認証ありがとうございます</h1>
          <div className="flex items-center justify-center text-5xl">
            🎉
          </div>
          <div>
            引き続き買い物をお楽しみください
          </div>
          <Link href={returnTo}>
            <Button primary block>戻る</Button>
          </Link>
        </div>
      )}
      {failed && !sent && (
        <div className="flex flex-col gap-4">
          <h1 className="font-bold text-center">メールアドレスの認証ができませんでした</h1>
          <div className="flex items-center justify-center text-5xl">
            😢
          </div>
          <div>
            お手数ですが、もう一度メールアドレスの認証をお願いします
          </div>
          <Button primary block onClick={handleResend}>再送信</Button>
        </div>
      )}
      {failed && sent && (
        <div className="flex flex-col gap-4">
          <h1 className="font-bold text-center">再送信しました</h1>
          <div className="flex items-center justify-center text-5xl">
            ✉️
          </div>
          <div>
            お手数ですが、もう一度メールアドレスの認証をお願いします
          </div>
        </div>
      )}
    </div>
  );
};
